import { Phone, Mail, Clock, MapPin, ArrowUpRight, AlertTriangle } from "lucide-react";
import { FadeUp } from "./Reveal";
import { DOCTOR } from "../data/content";
import { useLang } from "../i18n/i18n";

export function ContactSection({ withHeading = false }) {
  const { t } = useLang();
  const c = t.contact;
  return (
    <div data-testid="contact-section">
      {withHeading && (
        <div className="mx-auto mb-16 max-w-[1500px] px-6 md:px-12">
          <FadeUp>
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">{c.kicker}</span>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-white md:text-6xl">{c.title}</h2>
          </FadeUp>
        </div>
      )}

      <section data-testid="contact-primary" className="relative overflow-hidden bg-ink px-6 py-16 md:px-12 md:py-24">
        <div className="pointer-events-none absolute right-0 top-0 h-[500px] w-[500px] rounded-full bg-crimson/10 blur-[150px]" />
        <div className="relative mx-auto max-w-[1500px] space-y-14">
          <FadeUp>
            <a href={DOCTOR.phoneHref} data-testid="contact-call-link" className="group block border-b border-white/10 pb-10">
              <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-crimson">
                <Phone className="h-4 w-4" /> {c.call}
              </div>
              <div className="mt-5 flex items-center justify-between gap-4">
                <span className="font-serif text-[9vw] font-light text-white transition-colors duration-300 group-hover:text-crimson sm:text-6xl md:text-8xl">
                  {DOCTOR.phone}
                </span>
                <ArrowUpRight className="h-8 w-8 shrink-0 text-white/30 transition-all duration-300 group-hover:text-crimson md:h-14 md:w-14" />
              </div>
            </a>
          </FadeUp>

          <FadeUp delay={0.1}>
            <a href={`mailto:${DOCTOR.email}`} data-testid="contact-email-link" className="group block border-b border-white/10 pb-10">
              <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-crimson">
                <Mail className="h-4 w-4" /> {c.email}
              </div>
              <div className="mt-5 flex items-center justify-between gap-4">
                <span className="break-all font-serif text-3xl font-light text-white transition-colors duration-300 group-hover:text-crimson sm:text-5xl md:text-7xl">
                  {DOCTOR.email}
                </span>
                <ArrowUpRight className="h-8 w-8 shrink-0 text-white/30 transition-all duration-300 group-hover:text-crimson md:h-14 md:w-14" />
              </div>
            </a>
          </FadeUp>
        </div>
      </section>

      <section className="bg-ink2 px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto grid max-w-[1500px] gap-6 md:grid-cols-3">
          <FadeUp>
            <div className="h-full border border-white/10 bg-ink p-8">
              <Clock className="h-6 w-6 text-crimson" />
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">{c.hoursLabel}</p>
              <p className="mt-2 font-serif text-4xl font-light text-white">{DOCTOR.hours}</p>
              <p className="mt-2 text-sm text-white/50">{c.hoursNote}</p>
            </div>
          </FadeUp>

          <FadeUp delay={0.08}>
            <div className="h-full border border-white/10 bg-ink p-8">
              <MapPin className="h-6 w-6 text-crimson" />
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">{c.locLabel}</p>
              <p className="mt-2 font-serif text-2xl font-light leading-snug text-white">{c.locName}</p>
              <p className="mt-2 text-sm text-white/50">{c.locAddr}</p>
            </div>
          </FadeUp>

          <FadeUp delay={0.16}>
            <a
              href={DOCTOR.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-maps-link"
              className="group flex h-full flex-col justify-between border border-white/10 bg-crimson p-8 transition-colors duration-300 hover:bg-crimson-muted"
            >
              <div>
                <MapPin className="h-6 w-6 text-white" />
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">{c.dirLabel}</p>
                <p className="mt-2 font-serif text-3xl font-light text-white">{c.maps}</p>
              </div>
              <ArrowUpRight className="mt-8 h-8 w-8 text-white transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </FadeUp>
        </div>

        <FadeUp delay={0.1}>
          <div data-testid="emergency-notice" className="mx-auto mt-6 flex max-w-[1500px] items-start gap-4 border border-crimson/40 bg-crimson/10 p-6">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-crimson" />
            <p className="text-sm leading-relaxed text-white/80">
              <span className="font-semibold text-white">{c.emergency}</span> {c.emergencyBody}
            </p>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
